import type { Permission } from "@nexseed/shared";
import {
  Boxes,
  DatabaseZap,
  Mail,
  MapPinned,
  Sprout,
  Users,
  Warehouse,
  type LucideIcon,
} from "lucide-react";
import type { MasterKey, MasterSpec } from "./spec";
import { lossTypes, yearCodes } from "./specs/finance";
import {
  classifications,
  cropCategories,
  crops,
  spDecodeMap,
  varieties,
} from "./specs/materials";
import { facilities } from "./specs/plants";
import { vendors } from "./specs/partners";
import { mpTypes, packLayouts } from "./specs/packaging";
import { operators } from "./specs/operators";
import { customers } from "./specs/customers";
import { processingResources, treatmentSchemas } from "./specs/processing";
import { emailCc } from "./specs/email-cc";
import { productionPersonnel } from "./specs/personnel";
import { qcAnalysts } from "./specs/qc-analysts";
import {
  storesClassifications,
  storesItems,
  storesParties,
  storesUoms,
} from "./specs/stores";

/** Master screens registered for navigation, forms, lookups, and permission gates. */
export const MASTERS: Record<MasterKey, MasterSpec> = {
  facilities,
  "crop-categories": cropCategories,
  crops,
  classifications,
  varieties,
  "sp-decode-map": spDecodeMap,
  vendors,
  "year-codes": yearCodes,
  "loss-types": lossTypes,
  "processing-resources": processingResources,
  operators,
  "treatment-schemas": treatmentSchemas,
  "pack-layouts": packLayouts,
  "mp-types": mpTypes,
  "production-personnel": productionPersonnel,
  "email-cc": emailCc,
  "stores-uoms": storesUoms,
  "stores-classifications": storesClassifications,
  "stores-items": storesItems,
  "stores-parties": storesParties,
  "qc-analysts": qcAnalysts,
  customers,
};
export const isMasterKey = (k: string): k is MasterKey => k in MASTERS;

/**
 * Screens that are masters to the operator but not generic CRUD: the warehouse tree and the
 * variety-pair restrictions both have their own page. They are listed here so the rail can show
 * them alongside the registry masters without hard-coding two extra entries in nav.ts.
 */
export interface SpecialScreen {
  title: string;
  to: string;
  icon: LucideIcon;
  /** Its own view code — these are not registry masters, so they are not covered by one. */
  permission: Permission;
}
export const SPECIAL_SCREENS: SpecialScreen[] = [
  {
    title: "Warehouses",
    to: "/masters/locations",
    icon: Warehouse,
    permission: "locations.view",
  },
  {
    title: "Storage restrictions",
    to: "/masters/storage-restrictions",
    icon: Boxes,
    permission: "storage_restrictions.view",
  },
  // Each plant's own mail account (user, 2026-10-15): invites, codes and arrival e-mails.
  {
    title: "Mail Settings",
    to: "/masters/mail-settings",
    icon: Mail,
    permission: "mail_settings.view",
  },
  // Every lot the S-Capture / COB / Core PDN feeds sent, read-only (user, 2026-10-15).
  {
    title: "API Feed Data",
    to: "/masters/api-feed",
    icon: DatabaseZap,
    permission: "pdn_inbox.view",
  },
  // The Grow-Out Test's two legacy masters (QCS / QCM Masters menu, manual p150–152, p214–215).
  // Both open on got.locations (the Farmer Master's form is UNKNOWN in the map; screen only).
  {
    title: "GOT Location Master",
    to: "/masters/got-locations",
    icon: MapPinned,
    permission: "got.locations",
  },
  {
    title: "GOT Farmer Master",
    to: "/masters/got-farmers",
    icon: Users,
    permission: "got.locations",
  },
  // Each crop's Standard PPN and Days to Observation (user, 2026-11-03), on crops.got_standard_ppn
  // and crops.got_observation_days.
  {
    title: "GOT Crop Standards",
    to: "/masters/got-ppn-standards",
    icon: Sprout,
    permission: "got.locations",
  },
];
